<template>
    <page :tabs="tabs">
        <grid
            ref="grid"
            :fetchCallback="fetchCallback"
            :columnDefs="columnDefs"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowStyle="rowStyle"
            :isAutoWidth="true"
            @on-total-records="setTotalRecords"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :range-months-limit="12"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown
                :icon="['fas', 'filter']"
                :items="actionFilterOptions"
                variant="primary"
                :responsiveness="false"
            >
                {{ actionFilterLabel }}
            </responsive-dropdown>
        </grid>
    </page>
</template>

<script>
import API from '../../../api';
import page from '../../generic/page/page.vue';
import Grid from '../../generic/grid/grid.vue';
import format from 'date-fns/esm/format/index';
import responsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import DateRangePicker, {
    LAST_WEEK,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_WEEK,
    THIS_MONTH,
    THIS_YEAR,
    ALL_DATES,
    TODAY,
    YESTERDAY,
} from '../../generic/date-range-picker/date-range-picker.vue';
import { mapGetters } from 'vuex';

export default {
    components: { responsiveDropdown, page, Grid, DateRangePicker },
    name: 'OwnerListLogs',
    data() {
        return {
            dateRange: nameToRange(THIS_YEAR),
            dateRequestFormat: 'yyyy-MM-dd',
            gridApi: {},
            rowData: [],
            actionFilter: '',
            actionFilterLabel: 'All',
            rowStyle: null,
            predefinedRanges: [
                YESTERDAY,
                TODAY,
                LAST_WEEK,
                THIS_WEEK,
                LAST_MONTH,
                THIS_MONTH,
                LAST_YEAR,
                THIS_YEAR,
                ALL_DATES,
            ],
            tabs: [
                {
                    title: 'Owner List',
                    route: '/app/owner/list/',
                    active: false,
                },
                {
                    title: 'Logs',
                    route: '/app/owner/list/logs/',
                    active: true,
                },
            ],
            totalRecords: 0,
        };
    },
    mounted() {
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        columnDefs() {
            return [
                {
                    headerName: '#',
                    minWidth: 50,
                    sortable: false,
                    valueGetter: (params) => {
                        return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                    },
                },
                {
                    field: 'created',
                    headerName: 'Date',
                    minWidth: 190,
                    maxWidth: 190,
                    sort: 'desc',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'user_id',
                    headerName: 'User ID',
                    maxWidth: 90,
                    minWidth: 90,
                },
                {
                    field: 'first_name',
                    headerName: 'First Name',
                    minWidth: 150,
                },
                {
                    field: 'last_name',
                    headerName: 'Last Name',
                    minWidth: 150,
                },
                {
                    field: 'email',
                    headerName: 'User Email',
                    minWidth: 300,
                    autoHeight: true,
                },
                {
                    field: 'initiated_by',
                    headerName: 'Initiated By',
                    minWidth: 300,
                    autoWidthExclude: true,
                },
                {
                    type: 'badgeColumn',
                    field: 'action_display',
                    headerName: '',
                    sortable: false,
                    minWidth: 120,
                    autoWidthExclude: true,
                    cellRendererParams: {
                        badgeVariant: ({ data }) => {
                            if (data.action === 'CREATED') return 'success';
                            else if (data.action === 'DELETED') return 'danger';
                            else return '';
                        },
                    },
                },
            ];
        },
        actionFilterOptions() {
            return [
                {
                    label: 'All',
                    click: () => {
                        this.actionFilter = '';
                        this.actionFilterLabel = 'All';
                        this.refresh();
                    },
                },
                {
                    label: 'Created',
                    click: () => {
                        this.actionFilter = 'CREATED';
                        this.actionFilterLabel = 'Created';
                        this.refresh();
                    },
                },
                {
                    label: 'Deleted',
                    click: () => {
                        this.actionFilter = 'DELETED';
                        this.actionFilterLabel = 'Deleted';
                        this.refresh();
                    },
                },
            ];
        },
        pinnedBottomRowData() {
            return [{}];
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(`/app/owner/log/grid/`, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        from_date: this.startDateRequest,
                        to_date: this.endDateRequest,
                        action: this.actionFilter,
                        search,
                    },
                });
            };
        },
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        setTotalRecords(value) {
            this.totalRecords = value;
        },
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
    },
};
</script>

<style lang="scss" scoped></style>
